export * as Cookie from './Cookie';
export * as Country from './Country';
export * as Currency from './Currency';
/**
 * Decoding utilities built on top of [io-ts](https://github.com/gcanti/io-ts).
 */
export * as Decoding from './Decoding';
export * as DeviceStorage from './DeviceStorage';
export * as Environment from './Environment';
/**
 * Fetching data from APIs can fail for different reasons. `ErrorAsync` type is meant to model those cases:
 *
 * - The service sent the wrong format (decoding failed).
 * - A client issue (4xx errors).
 * - A server issue (5xx errors).
 * - Some unexpected _unknown_ thing went wrong.
 *
 * ```ts
 * type ErrorAsync = Decoding | Client | Server | Unknown;
 * ```
 *
 * ### How do I use this?
 *
 * <!-- TODO: write a "How this old tides together?" guide and add here -->
 *
 * - For reporting errors use {@link ErrorAsync.notify}
 * - For predicates checks see {@link ErrorAsync.isClient}, {@link ErrorAsync.isDecoding},
 *  {@link ErrorAsync.isServer}, {@link ErrorAsync.isUnknown}
 * - For creating errors when making HTTP requests use {@link ErrorAsync.errorToLeft}
 * - For lower level control use {@link ErrorAsync.match}
 */
export * as ErrorAsync from './ErrorAsync';
/**
 * A client for communication between different JS bundles with different scopes. Example:
 * ```cw-buyer -> cw-assets```
 * or
 * ```cw-assets -> cw-buyer, cw-seller-center, cw-accounts```
 *
 * ### How do I use this?
 * We have a particular event and data that multiple bundles in our code can make use of.
 * We can use `EventBus` to post that data to all the subscribers of it. Here is an example of how
 * it works in code:
 *
 * Let say we want to share "UserData" across `cw-assets` and `cw-buyer`
 *
 * ```ts
 * // cw-buyer example
 * import { EventBus } from '@catawiki/runtime-toolkit';
 *
 * interface UserData {
 *   name: string;
 *   token: string;
 * }
 *
 * useEffect(() => {
 *     const userDataCallback: EventBus.EventBusCallback<UserData> = ({ detail }) => {
 *        someAction(detail.name);
 *        someOtherAction(detail.token);
 *     }
 *
 *     EventBus.subscribe<UserData>('user-data-channel', userDataCallback);
 *
 *     return () => EventBus.unsubscribe('user-data-channel', userDataCallback);
 * }, [])
 *
 * // cw-assets example
 * import { EventBus } from '@catawiki/runtime-toolkit';
 *
 * EventBus.post('user-data-channel', { name: 'John', token: 'cdbRshkuCsqa' });
 * ```
 *
 * As soon as we post the data `cw-buyer` will receive the event. The main functionality that this tool adds is
 * making sure that `cw-buyer` will receive the events that have already happened before it had a chance of subscribing
 * to the event. They are being stored in an array in `window.cwMemoryStore`. So as soon as `cw-buyer` subscribed it
 * will immediately fire the callback on the events already stored. And of course it will listen to new events
 * happening for that channel.
 */
export * as EventBus from './EventBus';
export * as FetchMock from './FetchMock';
/**
 * Http module implementing a pipe based builder pattern to create requests.
 *
 * A configuration object is constructed to then trigger a request with it.
 *
 * ```ts
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/function';
 *
 * const get = pipe(
 *   Http.get('/foo/bar'),
 *   Http.setSearch({ foo: 'bar' }),
 *   Http.addHeader('authorization', 'Bearer SomeToken'),
 * )
 * ```
 *
 * [send](/docs/modules/http/HttpClient.ts.md#send) takes the configuration and
 * performs the request
 *
 * ```ts
 * Http.send<Json>()(Http.get)
 *   .then(res => {...})
 *   .catch(err => {...});
 * ```
 *
 * Or using pipe
 *
 * ```ts
 * pipe(Http.get, Http.send<Json>())
 *   .then(res => {...})
 *   .catch(err => {...});
 * ```
 *
 * ### Examples
 *
 * Simple GET request with search query:
 *
 * ```ts
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/function';
 *
 * type Json = Record<string, unknown>;
 *
 * pipe(
 *   Http.get('/foo/bar'),
 *   Http.setSearch({ foo: 'bar' }),
 *   Http.send<Json>(),
 * )
 *   .then(res => {...})
 *   .catch(err => {...});
 *
 * ```
 *
 * Simple POST request with JSON body
 *
 * ```ts
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/function';
 *
 * type Json = Record<string, unknown>;
 *
 * pipe(
 *   Http.postJson('/foo/bar', { foo: 'bar' }),
 *   Http.send<Json>(),
 * )
 *   .then(res => {...})
 *   .catch(err => {...});
 * ```
 *
 * A more complext PUT request with headers, search query and Url encoded body.
 *
 * ```ts
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/pipeable';
 *
 * type Json = Record<string, unknown>;
 *
 * pipe(
 *   Http.putUrlencoded('/foo/bar', { foo: 'bar' }),
 *   Http.setSearch({ foo: 'bar' }),
 *   Http.addHeader('authorization', 'Bearer SomeToken'),
 *   Http.addHeader('x-foo', 'some other header'),
 *   Http.send<Json>()
 * )
 *   .then(res => {...})
 *   .catch(err => {...});
 * ```
 *
 * Or extracting the request logic into a function:
 *
 * ```ts
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/pipeable';
 *
 * type Json = Record<string, unknown>;
 *
 * const putFoo = (payload: Json, search: Http.UrlSearch): Promise<Json> =>
 *   pipe(
 *     Http.putUrlencoded('/foo/bar', payload),
 *     Http.setSearch(search),
 *     Http.addHeader('authorization', 'Bearer SomeToken'),
 *     Http.addHeader('x-foo', 'some other header'),
 *     Http.send<Json>()
 *   );
 *
 * putFoo('url', { foo: 'bar' })
 *   .then(res => {...})
 *   .catch(err => {...});
 * ```
 *
 * And one last example, setting a custom body parser.
 *
 * ```ts
 * import { Http } from '@catawiki/runtime-toolkit';
 * import { pipe } from 'fp-ts/pipeable';
 *
 * type Json = Record<string, unknown>;
 *
 * pipe(
 *   Http.get('/foo/bar'),
 *   Http.setBodyParser(() => 'arrayBuffer'),
 *   Http.send<ArrayBuffer>()
 * )
 *   .then(res => {...})
 *   .catch(err => {...});
 * ```
 */
export * as Http from './http';
export * as Image from './Image';
export * as LangUri from './LangUri';
export * as Locale from './Locale';
export * as Maths from './Maths';
export * as Money from './Money';
export * as Observability from './Observability';
export * as Print from './Print';
export * as Promises from './Promises';
export * as Query from './Query';
export * as Test from './Test';
export * as Time from './Time';
export * as Tracking from './Tracking';
/**
 * States of the data fetched asynchronously from a remote resourse can be different (before, while, and after fetching it).
 *
 * The `RemoteData` type models that as an union type.
 *
 * ```ts
 * type RemoteData<Data> = Idle | Loading | Success<Data> | Failure;
 * ```
 */
export * as RemoteData from './RemoteData';
export * as Url from './Url';
/**
 * A simple validation functionality for validating a field/value Object
 *
 * @example
 *
 * ```ts
 * import { Validation } from "@catawiki/runtime-toolkit";
 *
 * const validator = Validation.createValidator({
 *   firstName: [
 *     [ Validation.required, 'This field is required' ]
 *     [ Validation.validLength(24), 'Max length 24 characters' ]
 *   ],
 *   dateOfBirth: [
 *     [ Validation.required, 'This field is required' ],
 *     [ Validation.validDate, 'Please select a valid date' ],
 *   ]
 * });
 *
 * const result = validator({
 *   firstName: '',
 *   dateOfBirth: '1986-09-11',
 * });
 *
 * // If there are errors it returns an Object with fieldName/Array of errorMessages pairs:
 *
 * // {
 * //  firstName: ["This field is required"]
 * // }
 *
 * // If there are no errors it returns null.
 * ```
 */
export * as Validation from './Validation';
export * as WebVitals from './WebVitals';
export * from './constants';
export * from './hooks';
export * from './utils';
export * from './types';
